import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import sentinelIcon from "@/assets/sentinelhub-icon.svg";
import { useTheme } from "@/hooks/useTheme";
import { useLanguage } from "@/i18n/LanguageContext";
import { disclosureSeo } from "@/i18n/seo";
import { LANG_HTML, LOCALE, SITE_URL, localizedPath } from "@/lib/routes";

type Lang = "pt" | "es" | "en";

const T: Record<Lang, {
  metaTitle: string;
  metaDesc: string;
  ogDesc: string;
  back: string;
  h1a: string;
  h1b: string;
  h1c: string;
  tagline: string;
  summaryLabel: string;
  summary: string;
  sReport: string;
  reportIntro: string;
  reportInclude: string;
  reportList: string[];
  reportWarn: React.ReactNode;
  sScope: string;
  scopeIntro: string;
  scopeList: (string | React.ReactNode)[];
  scopeOut: React.ReactNode;
  sAllowed: string;
  allowedP1: string;
  allowedP2: string;
  sForbidden: string;
  forbiddenIntro: string;
  forbiddenList: string[];
  sCommit: string;
  commitIntro: string;
  commitList: string[];
  sPublic: string;
  publicP1: string;
  publicP2: string;
  sReward: string;
  rewardP1: string;
  rewardP2: string;
  sGoodFaith: string;
  gfP1: string;
  gfP2: string;
  sContact: string;
  contactIntro: string;
  copyright: string;
}> = {
  pt: {
    metaTitle: "Política de Divulgação Responsável — SentinelHub",
    metaDesc: "Política de divulgação responsável de vulnerabilidades da SentinelHub: como reportar, escopo, testes permitidos e compromissos.",
    ogDesc: "Como reportar vulnerabilidades de segurança para a SentinelHub de forma responsável.",
    back: "Voltar ao site",
    h1a: "Política de ",
    h1b: "Divulgação Responsável",
    h1c: " de Vulnerabilidades",
    tagline: "SentinelHub · Segurança em primeiro lugar",
    summaryLabel: "Resumo",
    summary: "A SentinelHub valoriza a colaboração de pesquisadores de segurança, clientes e membros da comunidade na identificação responsável de vulnerabilidades que possam afetar nossos sistemas. Caso você identifique uma possível vulnerabilidade em nosso site, plataforma, APIs ou serviços, solicitamos que nos comunique de forma privada antes de qualquer divulgação pública.",
    sReport: "Como reportar",
    reportIntro: "Envie o relatório para:",
    reportInclude: "Inclua, sempre que possível:",
    reportList: [
      "Descrição detalhada da vulnerabilidade;",
      "Endereço ou recurso afetado;",
      "Passos necessários para reproduzir o problema;",
      "Evidências, capturas de tela ou registros técnicos;",
      "Possível impacto identificado;",
      "Sugestão de correção, quando aplicável;",
      "Seus dados de contato.",
    ],
    reportWarn: (<><strong>Não envie</strong> senhas, tokens, chaves privadas ou informações pessoais desnecessárias.</>),
    sScope: "Escopo",
    scopeIntro: "Esta política se aplica aos seguintes ativos:",
    scopeList: [
      <code className="rounded bg-secondary/60 px-1.5 py-0.5 font-mono text-sm text-primary">https://sentinelhub.com.br</code>,
      "Subdomínios oficialmente operados pela SentinelHub;",
      "Plataforma web da SentinelHub;",
      "APIs oficialmente disponibilizadas pela SentinelHub.",
    ],
    scopeOut: (<>Sistemas, endereços IP, domínios e ambientes pertencentes aos nossos clientes <strong>não fazem parte</strong> desta autorização.</>),
    sAllowed: "Testes permitidos",
    allowedP1: "São permitidos testes de segurança realizados de boa-fé, sem causar indisponibilidade, prejuízo, exposição de dados ou impacto aos usuários.",
    allowedP2: "O pesquisador deve limitar os testes ao mínimo necessário para confirmar a existência da vulnerabilidade.",
    sForbidden: "Atividades proibidas",
    forbiddenIntro: "Não são autorizadas:",
    forbiddenList: [
      "Interrupção ou degradação dos serviços;",
      "Ataques de negação de serviço;",
      "Engenharia social contra funcionários, clientes ou fornecedores;",
      "Tentativas de obtenção de credenciais;",
      "Persistência em sistemas;",
      "Alteração, exclusão ou destruição de dados;",
      "Download massivo de informações;",
      "Acesso a dados de terceiros além do mínimo necessário para comprovação;",
      "Testes em ativos pertencentes aos clientes da SentinelHub;",
      "Publicação da vulnerabilidade antes da conclusão do processo de correção.",
    ],
    sCommit: "Nosso compromisso",
    commitIntro: "Ao receber um relatório válido, a SentinelHub buscará:",
    commitList: [
      "Confirmar o recebimento do relato;",
      "Avaliar tecnicamente a vulnerabilidade;",
      "Manter comunicação com o pesquisador;",
      "Priorizar a correção conforme o nível de risco;",
      "Informar quando o problema tiver sido corrigido.",
    ],
    sPublic: "Divulgação pública",
    publicP1: "Solicitamos que nenhuma informação seja divulgada publicamente antes que a SentinelHub tenha concluído a investigação e implementado as medidas corretivas necessárias.",
    publicP2: "A divulgação poderá ser acordada entre a SentinelHub e o pesquisador após a correção.",
    sReward: "Reconhecimento e recompensas",
    rewardP1: "Atualmente, a SentinelHub não mantém um programa formal de recompensa financeira por vulnerabilidades.",
    rewardP2: "A empresa poderá, a seu critério, reconhecer publicamente pesquisadores que tenham contribuído de forma responsável, desde que exista autorização expressa para isso.",
    sGoodFaith: "Boa-fé",
    gfP1: "Pesquisadores que cumprirem esta política e atuarem de boa-fé não serão tratados como responsáveis por acesso indevido decorrente exclusivamente dos testes autorizados por esta política.",
    gfP2: "Esta proteção não se aplica a ações que causem danos, violem a legislação, afetem terceiros ou ultrapassem os limites estabelecidos neste documento.",
    sContact: "Contato",
    contactIntro: "Dúvidas ou relatos de segurança devem ser enviados para:",
    copyright: "© 2026 SentinelHub. Todos os direitos reservados.",
  },
  es: {
    metaTitle: "Política de Divulgación Responsable — SentinelHub",
    metaDesc: "Política de divulgación responsable de vulnerabilidades de SentinelHub: cómo reportar, alcance, pruebas permitidas y compromisos.",
    ogDesc: "Cómo reportar vulnerabilidades de seguridad a SentinelHub de forma responsable.",
    back: "Volver al sitio",
    h1a: "Política de ",
    h1b: "Divulgación Responsable",
    h1c: " de Vulnerabilidades",
    tagline: "SentinelHub · Seguridad ante todo",
    summaryLabel: "Resumen",
    summary: "SentinelHub valora la colaboración de investigadores de seguridad, clientes y miembros de la comunidad en la identificación responsable de vulnerabilidades que puedan afectar a nuestros sistemas. Si identificas una posible vulnerabilidad en nuestro sitio, plataforma, APIs o servicios, te pedimos que nos la comuniques de forma privada antes de cualquier divulgación pública.",
    sReport: "Cómo reportar",
    reportIntro: "Envía el reporte a:",
    reportInclude: "Incluye, siempre que sea posible:",
    reportList: [
      "Descripción detallada de la vulnerabilidad;",
      "Dirección o recurso afectado;",
      "Pasos necesarios para reproducir el problema;",
      "Evidencias, capturas de pantalla o registros técnicos;",
      "Posible impacto identificado;",
      "Sugerencia de corrección, cuando corresponda;",
      "Tus datos de contacto.",
    ],
    reportWarn: (<><strong>No envíes</strong> contraseñas, tokens, claves privadas ni información personal innecesaria.</>),
    sScope: "Alcance",
    scopeIntro: "Esta política se aplica a los siguientes activos:",
    scopeList: [
      <code className="rounded bg-secondary/60 px-1.5 py-0.5 font-mono text-sm text-primary">https://sentinelhub.com.br</code>,
      "Subdominios oficialmente operados por SentinelHub;",
      "Plataforma web de SentinelHub;",
      "APIs oficialmente disponibles por SentinelHub.",
    ],
    scopeOut: (<>Los sistemas, direcciones IP, dominios y entornos pertenecientes a nuestros clientes <strong>no forman parte</strong> de esta autorización.</>),
    sAllowed: "Pruebas permitidas",
    allowedP1: "Se permiten pruebas de seguridad realizadas de buena fe, sin causar indisponibilidad, perjuicio, exposición de datos o impacto a los usuarios.",
    allowedP2: "El investigador debe limitar las pruebas al mínimo necesario para confirmar la existencia de la vulnerabilidad.",
    sForbidden: "Actividades prohibidas",
    forbiddenIntro: "No están autorizadas:",
    forbiddenList: [
      "Interrupción o degradación de los servicios;",
      "Ataques de denegación de servicio;",
      "Ingeniería social contra empleados, clientes o proveedores;",
      "Intentos de obtención de credenciales;",
      "Persistencia en sistemas;",
      "Alteración, eliminación o destrucción de datos;",
      "Descarga masiva de información;",
      "Acceso a datos de terceros más allá del mínimo necesario para comprobación;",
      "Pruebas en activos pertenecientes a clientes de SentinelHub;",
      "Publicación de la vulnerabilidad antes de la conclusión del proceso de corrección.",
    ],
    sCommit: "Nuestro compromiso",
    commitIntro: "Al recibir un reporte válido, SentinelHub buscará:",
    commitList: [
      "Confirmar la recepción del reporte;",
      "Evaluar técnicamente la vulnerabilidad;",
      "Mantener comunicación con el investigador;",
      "Priorizar la corrección según el nivel de riesgo;",
      "Informar cuando el problema haya sido corregido.",
    ],
    sPublic: "Divulgación pública",
    publicP1: "Solicitamos que ninguna información se divulgue públicamente antes de que SentinelHub haya concluido la investigación e implementado las medidas correctivas necesarias.",
    publicP2: "La divulgación podrá acordarse entre SentinelHub y el investigador tras la corrección.",
    sReward: "Reconocimiento y recompensas",
    rewardP1: "Actualmente, SentinelHub no mantiene un programa formal de recompensas económicas por vulnerabilidades.",
    rewardP2: "La empresa podrá, a su criterio, reconocer públicamente a los investigadores que hayan contribuido de forma responsable, siempre que exista autorización expresa para ello.",
    sGoodFaith: "Buena fe",
    gfP1: "Los investigadores que cumplan esta política y actúen de buena fe no serán tratados como responsables por acceso indebido derivado exclusivamente de las pruebas autorizadas por esta política.",
    gfP2: "Esta protección no se aplica a acciones que causen daños, violen la legislación, afecten a terceros o superen los límites establecidos en este documento.",
    sContact: "Contacto",
    contactIntro: "Dudas o reportes de seguridad deben enviarse a:",
    copyright: "© 2026 SentinelHub. Todos los derechos reservados.",
  },
  en: {
    metaTitle: "Responsible Disclosure Policy — SentinelHub",
    metaDesc: "SentinelHub's responsible vulnerability disclosure policy: how to report, scope, allowed testing, and commitments.",
    ogDesc: "How to report security vulnerabilities to SentinelHub responsibly.",
    back: "Back to site",
    h1a: "",
    h1b: "Responsible Disclosure",
    h1c: " Policy for Vulnerabilities",
    tagline: "SentinelHub · Security first",
    summaryLabel: "Summary",
    summary: "SentinelHub values the collaboration of security researchers, customers, and community members in the responsible identification of vulnerabilities that may affect our systems. If you identify a potential vulnerability in our website, platform, APIs, or services, we ask that you communicate it to us privately before any public disclosure.",
    sReport: "How to report",
    reportIntro: "Send the report to:",
    reportInclude: "Whenever possible, include:",
    reportList: [
      "Detailed description of the vulnerability;",
      "Affected address or resource;",
      "Steps required to reproduce the issue;",
      "Evidence, screenshots, or technical logs;",
      "Potential impact identified;",
      "Suggested fix, when applicable;",
      "Your contact information.",
    ],
    reportWarn: (<><strong>Do not send</strong> passwords, tokens, private keys, or unnecessary personal information.</>),
    sScope: "Scope",
    scopeIntro: "This policy applies to the following assets:",
    scopeList: [
      <code className="rounded bg-secondary/60 px-1.5 py-0.5 font-mono text-sm text-primary">https://sentinelhub.com.br</code>,
      "Subdomains officially operated by SentinelHub;",
      "SentinelHub web platform;",
      "APIs officially made available by SentinelHub.",
    ],
    scopeOut: (<>Systems, IP addresses, domains, and environments belonging to our customers <strong>are not part</strong> of this authorization.</>),
    sAllowed: "Allowed testing",
    allowedP1: "Security testing performed in good faith is permitted, without causing unavailability, harm, data exposure, or impact to users.",
    allowedP2: "The researcher must limit testing to the minimum necessary to confirm the existence of the vulnerability.",
    sForbidden: "Prohibited activities",
    forbiddenIntro: "The following are not authorized:",
    forbiddenList: [
      "Interruption or degradation of services;",
      "Denial-of-service attacks;",
      "Social engineering against employees, customers, or suppliers;",
      "Attempts to obtain credentials;",
      "Persistence in systems;",
      "Alteration, deletion, or destruction of data;",
      "Mass download of information;",
      "Access to third-party data beyond the minimum necessary for proof;",
      "Testing on assets belonging to SentinelHub customers;",
      "Public disclosure of the vulnerability before the remediation process is complete.",
    ],
    sCommit: "Our commitment",
    commitIntro: "Upon receiving a valid report, SentinelHub will strive to:",
    commitList: [
      "Confirm receipt of the report;",
      "Technically assess the vulnerability;",
      "Maintain communication with the researcher;",
      "Prioritize the fix based on risk level;",
      "Inform when the issue has been fixed.",
    ],
    sPublic: "Public disclosure",
    publicP1: "We ask that no information be publicly disclosed before SentinelHub has completed the investigation and implemented the necessary corrective measures.",
    publicP2: "Disclosure may be agreed between SentinelHub and the researcher after the fix.",
    sReward: "Recognition and rewards",
    rewardP1: "SentinelHub does not currently maintain a formal financial bug bounty program.",
    rewardP2: "The company may, at its discretion, publicly recognize researchers who have contributed responsibly, provided there is express authorization to do so.",
    sGoodFaith: "Good faith",
    gfP1: "Researchers who comply with this policy and act in good faith will not be treated as responsible for unauthorized access resulting exclusively from the testing authorized by this policy.",
    gfP2: "This protection does not apply to actions that cause damage, violate the law, affect third parties, or exceed the limits established in this document.",
    sContact: "Contact",
    contactIntro: "Security questions or reports should be sent to:",
    copyright: "© 2026 SentinelHub. All rights reserved.",
  },
};

const DivulgacaoResponsavel = () => {
  useTheme();
  const { lang } = useLanguage();
  const t = T[lang];
  const canonical = `${SITE_URL}${localizedPath("disclosure", lang)}`;
  const homePath = localizedPath("home", lang);

  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      <Helmet>
        <html lang={LANG_HTML[lang]} />
        <title>{t.metaTitle}</title>
        <meta name="description" content={t.metaDesc} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="pt-BR" href={`${SITE_URL}${localizedPath("disclosure", "pt")}`} />
        <link rel="alternate" hrefLang="en" href={`${SITE_URL}${localizedPath("disclosure", "en")}`} />
        <link rel="alternate" hrefLang="es" href={`${SITE_URL}${localizedPath("disclosure", "es")}`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${localizedPath("disclosure", "pt")}`} />
        <meta property="og:title" content={t.metaTitle} />
        <meta property="og:description" content={t.ogDesc} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content={LOCALE[lang]} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={t.metaTitle} />
        <meta name="twitter:description" content={t.ogDesc} />
      </Helmet>

      {/* Top nav */}
      <div className="border-b border-border/50">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link
            to={homePath}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t.back}
          </Link>
          <div className="inline-flex items-center gap-2">
            <img src={sentinelIcon} alt="SentinelHub Security Platform" className="h-6 w-6" />
            <span className="text-base font-bold tracking-tight">
              Sentinel<span className="text-primary">Hub</span>
            </span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <header className="relative border-b border-border bg-gradient-to-br from-secondary/40 via-background to-background">
        <div className="max-w-3xl mx-auto px-6 py-14">
          <h1 className="text-3xl md:text-4xl font-display font-bold leading-tight mb-3">
            {t.h1a}<span className="text-primary">{t.h1b}</span>{t.h1c}
          </h1>
          <p className="text-sm text-muted-foreground font-mono">{t.tagline}</p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 space-y-10">
        <section className="rounded-xl border-l-4 border-primary bg-primary/5 p-6">
          <h2 className="text-xs uppercase tracking-wider font-bold text-primary mb-3 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" /> {t.summaryLabel}
          </h2>
          <p className="text-sm text-muted-foreground">{t.summary}</p>
        </section>

        <Section title={t.sReport}>
          <p>{t.reportIntro}</p>
          <p>
            <a href="mailto:contato@sentinelhub.com.br" className="text-primary hover:underline font-semibold">
              contato@sentinelhub.com.br
            </a>
          </p>
          <p>{t.reportInclude}</p>
          <ul>
            {t.reportList.map((li, i) => <li key={i}>{li}</li>)}
          </ul>
          <p>{t.reportWarn}</p>
        </Section>

        <Section title={t.sScope}>
          <p>{t.scopeIntro}</p>
          <ul>
            {t.scopeList.map((li, i) => <li key={i}>{li}</li>)}
          </ul>
          <p>{t.scopeOut}</p>
        </Section>

        <Section title={t.sAllowed}>
          <p>{t.allowedP1}</p>
          <p>{t.allowedP2}</p>
        </Section>

        <Section title={t.sForbidden}>
          <p>{t.forbiddenIntro}</p>
          <ul>
            {t.forbiddenList.map((li, i) => <li key={i}>{li}</li>)}
          </ul>
        </Section>

        <Section title={t.sCommit}>
          <p>{t.commitIntro}</p>
          <ul>
            {t.commitList.map((li, i) => <li key={i}>{li}</li>)}
          </ul>
        </Section>

        <Section title={t.sPublic}>
          <p>{t.publicP1}</p>
          <p>{t.publicP2}</p>
        </Section>

        <Section title={t.sReward}>
          <p>{t.rewardP1}</p>
          <p>{t.rewardP2}</p>
        </Section>

        <Section title={t.sGoodFaith}>
          <p>{t.gfP1}</p>
          <p>{t.gfP2}</p>
        </Section>

        <Section title={t.sContact}>
          <p>{t.contactIntro}</p>
          <p>
            <a href="mailto:contato@sentinelhub.com.br" className="text-primary hover:underline font-semibold">
              contato@sentinelhub.com.br
            </a>
          </p>
        </Section>
      </main>

      <footer className="border-t border-border">
        <div className="max-w-3xl mx-auto px-6 py-8 text-center text-sm text-muted-foreground">
          {t.copyright} ·{" "}
          <Link to="/" className="text-primary hover:underline">
            {t.back}
          </Link>
        </div>
      </footer>
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section>
    <h2 className="text-xl md:text-2xl font-display font-bold text-foreground border-b border-border pb-2 mb-4">
      {title}
    </h2>
    <div className="space-y-3 text-muted-foreground leading-relaxed [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:space-y-2 [&_strong]:text-foreground">
      {children}
    </div>
  </section>
);

export default DivulgacaoResponsavel;
